import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
} from 'react';
import {
  FileUpload,
  type FileUploadSelectEvent,
  type ItemTemplateOptions,
  type FileUploadHeaderTemplateOptions,
  type FileUploadHandlerEvent,
} from 'primereact/fileupload';
import Cancel from '@mui/icons-material/CancelOutlined';
import CheckCircle from '@mui/icons-material/CheckCircleOutline';
import uploadIcon from '../../../../assets/ic_upload_file.png';
import OpenAPI from '../../../../endpoint/Openapi';
import useTenantStore, { selectTenantId } from '../../../../stores/TenantStore';
import { Icons } from '../../../../assets/icons';
import { RadioButton } from 'primereact/radiobutton';
import { CustomColourPicker } from '../../../../components/CustomColourPicker';
import { type CustomError } from '../../../../models/Error';
import { Toast } from 'primereact/toast';
interface IFile {
  name: string;
  objectURL: string;
  size: number;
}

interface ResponseInterface {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any;
  error: unknown;
  response: Response;
}

export interface ILoginBackgroundImageUploadInstance {
  isFileSelected: () => boolean;
  save: () => Promise<ResponseInterface>;
}

export interface ILoginBackgroundImageUploadProps {
  disabled?: boolean;
}

const LoginBackgroundImageUpload = React.forwardRef<
  ILoginBackgroundImageUploadInstance,
  ILoginBackgroundImageUploadProps
>(({ disabled }, ref) => {
  const appAssets = useTenantStore((state) => state.appAssets);
  const tenantId = useTenantStore(selectTenantId);
  const [completed, setCompleted] = React.useState(false);
  const [totalSize, setTotalSize] = React.useState(0);
  const [selectedFile, setSelectedFile] = React.useState<File>();
  const [pending, setPending] = React.useState(false);
  const fileUploadRef = React.useRef<FileUpload>(null);
  const toast = React.useRef<Toast>(null);
  const currentImage = useMemo(
    () => appAssets?.find((a) => a.name === 'LoginBackground')?.url,
    [appAssets],
  );
  const [useImage, setUseImage] = React.useState<boolean>(false);
  const [selectedColor, setSelectedColor] = React.useState<string>();
  const currentUseImage = React.useRef(false);
  const currentColor = React.useRef<string>();

  useEffect(() => {
    const getColors = async () => {
      setPending(true);

      try {
        const res = await OpenAPI.GET('/api/v2/admin/tenant/login-bg', {
          params: { query: { tenantId } },
        });

        setUseImage(res.data?.useImage ?? false);
        setSelectedColor(res.data?.color ?? undefined);
        currentUseImage.current = res.data?.useImage ?? false;
        currentColor.current = res.data?.color ?? undefined;
      } catch {}

      setPending(false);
    };

    void getColors();
  }, [tenantId]);

  const handleSave = useCallback(async () => {
    setPending(true);

    try {
      const res = await OpenAPI.POST('/api/v2/admin/tenant/login-bg', {
        params: {
          query: {
            tenantId,
          },
        },
        body: {
          // @ts-expect-error
          File: selectedFile,
          UseImage: useImage,
          Color: selectedColor,
        },
        bodySerializer: (body) => {
          if (!body) return body;

          const formData = new FormData();
          if (body.File) formData.set('file', body.File);
          if (body.UseImage) formData.set('useImage', body.UseImage.toString());
          if (body.Color) formData.set('color', body.Color);
          return formData;
        },
      });

      setPending(false);
      setCompleted(true);
      currentUseImage.current = useImage;
      currentColor.current = selectedColor;
      return res;
    } catch (err) {
      setPending(false);
      toast.current?.show({
        severity: 'error',
        summary: 'Error occurred',
        detail:
          typeof err === 'string'
            ? err
            : err instanceof Error
              ? err.message
              : (err as CustomError)?.message || 'Unknown error',
        life: 3000,
      });
      throw err;
    }
  }, [useImage, selectedColor, selectedFile, tenantId]);

  useImperativeHandle(
    ref,
    () => ({
      isFileSelected: () =>
        (useImage !== currentUseImage.current ||
          (!useImage && selectedColor !== currentColor.current) ||
          (useImage && !!selectedFile)) &&
        !completed,
      save: handleSave as () => Promise<ResponseInterface>,
    }),
    [completed, handleSave, selectedColor, selectedFile, useImage],
  );

  const myUploader = (event: FileUploadHandlerEvent) => {
    setSelectedFile(event.files[0]);
    event.options.clear();
  };

  const onTemplateSelect = (e: FileUploadSelectEvent) => {
    setCompleted(false);
    setSelectedFile(e.files[0]);
    let _totalSize = totalSize;
    const files = e.files;

    files.forEach((file) => {
      _totalSize += file.size || 0;
    });

    setTotalSize(_totalSize);
  };

  const onTemplateRemove = (file: IFile, callback: () => void) => {
    setTotalSize(totalSize - file.size);
    callback();
    setSelectedFile(undefined);
    setCompleted(false);
  };

  const onTemplateClear = () => {
    setTotalSize(0);
  };

  const headerTemplate = (options: FileUploadHeaderTemplateOptions) => {
    return (
      <div className="flex flex-row mb-4 align-items-center">
        {options.chooseButton}
      </div>
    );
  };

  const itemTemplate = (file: object, props: ItemTemplateOptions) => {
    const fileName = (file as IFile).name;

    return (
      <div className="flex flex-col border rounded-2xl bg-whiteSmoke px-m16 py-m20">
        <div className="flex justify-between items-center">
          <div className="flex flex-1 items-center text-stormGray">
            <img
              loading="lazy"
              src={Icons.ic_png_file}
              className="aspect-1 w-[3rem] mr-1"
            />
            {fileName.length > 20
              ? fileName.substring(0, 18) + '...'
              : fileName}
          </div>

          <button
            onClick={() => {
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-expect-error
              onTemplateRemove(file as IFile, props.onRemove);
            }}
            // className="flex justify-center items-center border border-stormGray p-0.5 rounded-[2rem]"
          >
            {completed ? (
              <CheckCircle className="text-success" sx={{ fontSize: '2rem' }} />
            ) : (
              <Cancel className="text-stormGray" sx={{ fontSize: '2rem' }} />
            )}
          </button>
        </div>
      </div>
    );
  };

  const emptyTemplate = () => {
    return (
      <div className="flex flex-col items-center px-5 py-3.5 mt-5 text-sm leading-5 text-zinc-500">
        <img loading="lazy" src={uploadIcon} className="aspect-1 w-[60px]" />
        <div className="mt-11 text-base font-bold leading-6">
          drag and drop.
        </div>
        <div className="mt-2">Maximum file size is 100MB.</div>
        <div className="mt-12">File supported: PNG</div>
      </div>
    );
  };

  return (
    <div>
      <div className="flex flex-row">
        <RadioButton
          inputId="ingredient1"
          name="LoginBgType"
          value={true}
          onChange={(e) => {
            setUseImage(e.value);
            setCompleted(false);
          }}
          checked={useImage}
          className="relative mr-4 mt-3"
        />

        <div className="flex flex-col w-full text-base leading-6 text-black rounded-3xl">
          <FileUpload
            ref={fileUploadRef}
            name="icon[]"
            url="/api/upload"
            accept="image/png"
            maxFileSize={100000000}
            onSelect={onTemplateSelect}
            onError={onTemplateClear}
            onClear={onTemplateClear}
            headerTemplate={headerTemplate}
            itemTemplate={itemTemplate}
            emptyTemplate={emptyTemplate}
            chooseOptions={{
              label: 'Click to upload',
              icon: ' ',
              className: '',
            }}
            customUpload={true}
            uploadHandler={myUploader}
            disabled={pending || !!disabled || !useImage}
            contentClassName=" rounded-2xl border border-dashed border-neutral-400 px-0 py-0"
          />

          {currentImage && (
            <div className="flex bg-white border rounded-2xl border-neutral-300 mt-4 p-12 justify-center items-center">
              <img src={currentImage} className="object-contain" />
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-row items-end mt-6">
        <RadioButton
          inputId="ingredient2"
          name="LoginBgType"
          value={false}
          onChange={(e) => {
            setUseImage(e.value);
            setCompleted(false);
          }}
          checked={!useImage}
          className="relative mr-4 mb-3"
        />
        <CustomColourPicker
          colour={{
            type: 'LBg',
            displayName: 'Login Page Background',
            value: selectedColor ?? 'ffffff',
          }}
          onChange={(type, value) => {
            setSelectedColor(value);
            setCompleted(false);
          }}
          disabled={useImage}
        />
      </div>
      <Toast ref={toast} className="mt-header-height" />
    </div>
  );
});

LoginBackgroundImageUpload.displayName = 'LoginBackgroundImageUpload';

export default LoginBackgroundImageUpload;
